import { getLevelFromXp } from "@/lib/levels";
import { supabase } from "@/lib/supabase";

// XP values for different activities
export const XP_VALUES = {
  QUIZ_COMPLETED: 20,
  NOTE_CREATED: 10,
  FLASHCARDS_SESSION_COMPLETED: 10,
  FLASHCARDS_CREATED: 10,
  COACH_CONVERSATION: 5,
  STREAK_DAILY_BONUS: 5,
} as const;

// Positive feedback messages
export const POSITIVE_MESSAGES = {
  quiz_completed: ["Quiz réussi 🎯", "Bien joué 🔥", "Excellent travail 💪"],
  note_created: ["Fiche créée ✨", "Super initiative 📚", "Continue 🚀"],
  flashcards_session: ["Session terminée 💪", "Bien mémorisé 🧠", "Bravo 🎉"],
  flashcards_created: ["Flashcards créées ✨", "Super ! 🗂️", "Prêt à réviser 🚀"],
  coach_conversation: ["Bonne question ! 💬", "Tu progresses 📈", "Continue 🎯"],
} as const;

export type XpActionType = 
  | 'quiz_completed' 
  | 'note_created' 
  | 'flashcards_session' 
  | 'flashcards_created'
  | 'coach_conversation';

export interface AwardResult {
  success: boolean;
  xpAwarded: number;
  newXpTotal: number;
  level: number;
  levelName: string;
  leveledUp: boolean;
  previousLevel: number;
  isFirstActivityToday: boolean;
  newStreak: number;
  feedbackMessage: string;
  error?: string;
}

/**
 * Get a random positive feedback message
 */
export function getPositiveMessage(actionType: XpActionType): string {
  const messages = POSITIVE_MESSAGES[actionType];
  return messages[Math.floor(Math.random() * messages.length)];
}

/**
 * Awards XP to a user for completing an activity
 */
export async function awardXp(
  userId: string,
  actionType: XpActionType
): Promise<AwardResult> {
  const today = new Date().toISOString().split("T")[0];

  try {
    // 1. Get current user_stats
    const { data: currentStats, error: statsError } = await supabase
      .from("user_stats")
      .select("xp, streak_days, best_streak, last_activity_at")
      .eq("user_id", userId)
      .maybeSingle();

    if (statsError) {
      console.error("Error fetching user stats:", statsError);
      return createErrorResult(statsError.message);
    }

    const previousXp = currentStats?.xp || 0;
    const previousLevelInfo = getLevelFromXp(previousXp);

    // 2. Check if first activity today
    let isFirstActivityToday = true;
    if (currentStats?.last_activity_at) {
      const lastActivityDate = new Date(currentStats.last_activity_at).toISOString().split("T")[0];
      isFirstActivityToday = lastActivityDate !== today;
    }

    // 3. Calculate XP to add
    const baseXp = getBaseXpForAction(actionType);
    let totalXpToAdd = baseXp;

    // Add streak bonus if first activity of the day
    if (isFirstActivityToday) {
      totalXpToAdd += XP_VALUES.STREAK_DAILY_BONUS;
    }

    // 4. Calculate new streak
    let newStreak = 1;
    const currentBestStreak = currentStats?.best_streak || 0;

    if (currentStats?.last_activity_at) {
      const lastActivityDate = new Date(currentStats.last_activity_at).toISOString().split("T")[0];
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split("T")[0];

      if (lastActivityDate === today) {
        // Already active today - keep current streak
        newStreak = currentStats.streak_days || 1;
      } else if (lastActivityDate === yesterdayStr) {
        // Active yesterday - increment streak
        newStreak = (currentStats.streak_days || 0) + 1;
      } else {
        // Inactive for 2+ days - reset streak
        newStreak = 1;
      }
    }

    const newBestStreak = Math.max(currentBestStreak, newStreak);
    const newXpTotal = previousXp + totalXpToAdd;

    // 5. Upsert user_stats
    if (currentStats) {
      const { error: updateError } = await supabase
        .from("user_stats")
        .update({
          xp: newXpTotal,
          streak_days: newStreak,
          best_streak: newBestStreak,
          last_activity_at: new Date().toISOString(),
        })
        .eq("user_id", userId);

      if (updateError) {
        console.error("Error updating user stats:", updateError);
        return createErrorResult(updateError.message);
      }
      // ✅ Sync XP to profiles table (for dashboard display)
      await supabase.from("profiles").update({ xp: newXpTotal }).eq("id", userId);
    } else {
      const { error: insertError } = await supabase
        .from("user_stats")
        .insert({
          user_id: userId,
          xp: newXpTotal,
          streak_days: newStreak,
          best_streak: newStreak,
          last_activity_at: new Date().toISOString(),
        });

      if (insertError) {
        console.error("Error inserting user stats:", insertError);
        return createErrorResult(insertError.message);
      }
      // ✅ Sync XP to profiles table (for dashboard display)
      await supabase.from("profiles").update({ xp: newXpTotal }).eq("id", userId);
    }

    // 6. Calculate level info
    const newLevelInfo = getLevelFromXp(newXpTotal);
    const leveledUp = newLevelInfo.level > previousLevelInfo.level;

    console.log(`✅ XP awarded: +${totalXpToAdd} (${actionType}) | Total: ${newXpTotal} | Streak: ${newStreak}`);

    return {
      success: true,
      xpAwarded: totalXpToAdd,
      newXpTotal,
      level: newLevelInfo.level,
      levelName: newLevelInfo.levelName,
      leveledUp,
      previousLevel: previousLevelInfo.level,
      isFirstActivityToday,
      newStreak,
      feedbackMessage: getPositiveMessage(actionType),
    };
  } catch (error) {
    console.error("Error in awardXp:", error);
    return createErrorResult(error instanceof Error ? error.message : "Unknown error");
  }
}

function getBaseXpForAction(actionType: XpActionType): number {
  switch (actionType) {
    case 'quiz_completed':
      return XP_VALUES.QUIZ_COMPLETED;
    case 'note_created':
      return XP_VALUES.NOTE_CREATED;
    case 'flashcards_session':
    case 'flashcards_created':
      return XP_VALUES.FLASHCARDS_SESSION_COMPLETED;
    case 'coach_conversation':
      return XP_VALUES.COACH_CONVERSATION;
    default:
      return 0;
  }
}

function createErrorResult(error: string): AwardResult {
  return {
    success: false,
    xpAwarded: 0,
    newXpTotal: 0,
    level: 1,
    levelName: "Débutant",
    leveledUp: false,
    previousLevel: 1,
    isFirstActivityToday: false,
    newStreak: 0,
    feedbackMessage: "",
    error,
  };
}

/**
 * Format streak message for display
 */
export function formatStreakMessage(streak: number, isFirstToday: boolean): string {
  if (isFirstToday && streak > 1) {
    return `🔥 Série maintenue – jour ${streak}`;
  } else if (isFirstToday) {
    return `🔥 Nouvelle série commencée !`;
  }
  return `🔥 Série de ${streak} jour${streak > 1 ? 's' : ''}`;
}
